import { motion, useInView, type Variants, type UseInViewOptions } from "framer-motion";
import { useRef, type ReactNode } from "react";
import { fadeInUp } from "./animations";

interface AnimatedSectionProps {
  children: ReactNode;
  /** Animation variants (default: fadeInUp) */
  variants?: Variants;
  /** Viewport margin for triggering animation (default: "-100px") */
  viewportMargin?: UseInViewOptions["margin"];
  /** Whether to animate only once (default: true) */
  once?: boolean;
  /** Delay before animation starts in seconds */
  delay?: number;
  /** Additional className for the wrapper */
  className?: string;
}

/**
 * Reusable scroll-triggered animation wrapper.
 * Wraps children in a motion.div that animates when scrolled into view.
 */
export function AnimatedSection({
  children,
  variants = fadeInUp,
  viewportMargin = "-100px 0px -100px 0px",
  once = true,
  delay,
  className,
}: AnimatedSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once,
    margin: viewportMargin,
  });

  // Apply delay to variants if specified
  const variantsWithDelay = delay
    ? {
        ...variants,
        visible:
          typeof variants.visible === "object"
            ? {
                ...variants.visible,
                transition: {
                  ...(typeof variants.visible === "object" && "transition" in variants.visible
                    ? (variants.visible.transition as Record<string, unknown>)
                    : {}),
                  delay,
                },
              }
            : variants.visible,
      }
    : variants;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variantsWithDelay}
      className={className}
    >
      {children}
    </motion.div>
  );
}
